import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/LegalPages.css";

export default function PrivacySecurityPage() {
  return (
    <>
      <Header />
      <main className="legal-page">
        <div className="legal-page__inner">
          <div className="legal-card">
            <header>
              <div className="legal-page__pill">
                <span className="legal-page__pill-dot" />
                Gail Thompson Foundation
              </div>
              <h1 className="legal-page__title">
                Security &amp; Privacy Policy
              </h1>
              <p className="legal-page__meta">
                Last updated:{" "}
                <time dateTime="2025-11-23">November 23, 2025</time>
              </p>
            </header>

            <div className="legal-intro">
              <p>
                Gail Thompson Foundation (
                <strong>&quot;Gail Thompson Foundation&quot;</strong>,{" "}
                <strong>&quot;GTF&quot;</strong>, <strong>&quot;we&quot;</strong>,{" "}
                <strong>&quot;us&quot;</strong> or <strong>&quot;our&quot;</strong>
                ) respects your privacy and understands the importance of
                protecting your personal information. This Security &amp;
                Privacy Policy (the <strong>&quot;Policy&quot;</strong>) explains
                how we collect, use, share, and protect information when you
                interact with our website and online donation pages
                (collectively, the <strong>&quot;Site&quot;</strong>).
              </p>

              <p>
                By using the Site, you agree to the practices described in this
                Policy and in our{" "}
                <a href="/terms" aria-label="Read our Terms of Use">
                  Terms of Use
                </a>
                . If you do not agree, please do not use the Site.
              </p>
            </div>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                1. Who We Are and Scope of This Policy
              </h2>
              <p>
                Gail Thompson Foundation is a nonprofit organization dedicated
                to raising funds to support Alzheimer&apos;s disease research
                and dementia care programs in Maine in memory of Gail Thompson.
              </p>
              <p>
                This Policy applies to personal information we collect online
                through the Site and any emails or forms that link to this
                Policy. It does not apply to information collected offline (for
                example, at in-person events) or on websites we do not control,
                including third-party donation or event platforms that may be
                linked from our Site.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                2. Information We Collect
              </h2>

              <h3 className="legal-subheading">
                2.1 Information you provide to us
              </h3>
              <p>
                We collect information that you voluntarily provide, such as
                when you:
              </p>
              <ul>
                <li>Make a donation through the Site;</li>
                <li>Complete a contact form or send us an email;</li>
                <li>
                  Sign up to receive updates, newsletters, or event information;
                  or
                </li>
                <li>
                  Share stories, dedications, or other content related to our
                  mission.
                </li>
              </ul>
              <p>
                This information may include your name, email address, postal
                address, phone number, donation amount, donation preferences
                (for example, recurring gifts), dedication or tribute
                information, and any other details you choose to share.
              </p>
              <p>
                We do not intend to collect sensitive information such as
                medical records or detailed health information through the Site.
                Please do not submit protected health information or other
                highly sensitive personal information through our online forms.
              </p>

              <h3 className="legal-subheading">
                2.2 Information collected automatically
              </h3>
              <p>
                When you use the Site, we and our service providers may
                automatically collect certain information using cookies, log
                files, and similar technologies. This may include:
              </p>
              <ul>
                <li>IP address;</li>
                <li>Browser type and settings;</li>
                <li>Device identifiers;</li>
                <li>Pages you visit and the time and date of your visits;</li>
                <li>Referring/exit pages and clickstream data.</li>
              </ul>
              <p>
                We use this information to help operate, secure, and improve the
                Site, to understand how visitors engage with our content, and to
                detect or prevent fraudulent or malicious activity.
              </p>

              <h3 className="legal-subheading">
                2.3 Information from third parties
              </h3>
              <p>
                We may receive limited information about you from third-party
                service providers that support our work—for example, payment
                processors that confirm a donation, email services that manage
                mailing lists, or event platforms that manage registrations. We
                use this information only for the purposes described in this
                Policy.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                3. How We Use Your Information
              </h2>
              <p>
                We use the information we collect for purposes including:
              </p>
              <ul>
                <li>
                  Processing donations and issuing receipts and acknowledgments;
                </li>
                <li>Responding to your inquiries and requests;</li>
                <li>
                  Sending you updates about our work, impact, events, and
                  opportunities to support our mission (you can opt out of
                  non-essential emails at any time);
                </li>
                <li>
                  Operating, maintaining, and improving the Site and user
                  experience;
                </li>
                <li>
                  Analyzing trends and engagement to better understand our
                  supporters;
                </li>
                <li>
                  Protecting the security and integrity of our systems and
                  preventing fraud or abuse; and
                </li>
                <li>
                  Complying with legal, accounting, and reporting obligations.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                4. Legal Bases for Processing (Where Applicable)
              </h2>
              <p>
                Data protection laws in some jurisdictions, such as the European
                Economic Area or the United Kingdom, require us to explain the
                legal bases on which we process personal information. Where
                these laws apply, we generally rely on one or more of the
                following legal bases:
              </p>
              <ul>
                <li>
                  <strong>Consent</strong>, for example when you opt in to
                  receive our email updates;
                </li>
                <li>
                  <strong>Performance of a contract</strong>, for example when
                  we process a donation you choose to make;
                </li>
                <li>
                  <strong>Compliance with legal obligations</strong>, such as
                  retaining records for tax or accounting purposes; and
                </li>
                <li>
                  <strong>Legitimate interests</strong>, such as managing and
                  improving our fundraising efforts and communicating with
                  current donors and supporters.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                5. How We Share Your Information
              </h2>
              <p>
                We do not sell your personal information. We share information
                only as needed to operate our organization and fulfill our
                mission, including:
              </p>
              <ul>
                <li>
                  <strong>Service providers:</strong> We may share information
                  with trusted vendors that perform services on our behalf, such
                  as payment processing, website hosting, email delivery, data
                  storage, and analytics. These providers are authorized to use
                  your information only as needed to provide services to us.
                </li>
                <li>
                  <strong>With your direction or consent:</strong> If you ask us
                  to share your information with a particular organization (for
                  example, a partner you designate in connection with a gift),
                  we will do so as instructed.
                </li>
                <li>
                  <strong>Legal and safety reasons:</strong> We may share
                  information if required by law or if we reasonably believe
                  disclosure is necessary to protect our rights, the safety of
                  our staff, volunteers, or supporters, or to investigate fraud,
                  abuse, or security issues.
                </li>
                <li>
                  <strong>Organizational transitions:</strong> In the unlikely
                  event of a restructuring, merger, or similar event affecting
                  Gail Thompson Foundation, supporter information may be
                  transferred as part of that process, subject to appropriate
                  protections.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                6. Cookies and Similar Technologies
              </h2>
              <p>
                Cookies are small text files stored on your device that help us
                and our service providers recognize your browser and remember
                certain information. We may use:
              </p>
              <ul>
                <li>
                  <strong>Essential cookies</strong> to enable core features
                  like secure donation processing;
                </li>
                <li>
                  <strong>Performance or analytics cookies</strong> to
                  understand how visitors use the Site; and
                </li>
                <li>
                  <strong>Functional cookies</strong> to remember your
                  preferences.
                </li>
              </ul>
              <p>
                Most browsers allow you to control or disable cookies through
                your settings. If you disable cookies, some parts of the Site
                may not function properly.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                7. Online Donations and Payment Card Security
              </h2>
              <p>
                When you make an online donation, your payment is processed by a
                third-party payment processor (Stripe). Payment card details that you enter on the Site are
                transmitted directly to the payment processor over an encrypted
                connection.
              </p>
              <ul>
                <li>
                  We do <strong>not</strong> store your full payment card number
                  or security code on our servers.
                </li>
                <li>
                  We receive limited information related to the transaction,
                  such as the last four digits of your card, the type of card,
                  and the transaction amount, which we use for recordkeeping and
                  customer service.
                </li>
                <li>
                  Your payment card data is handled according to the payment
                  processor&apos;s own security standards and privacy policy.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                8. How We Protect Your Information
              </h2>
              <p>
                We use reasonable administrative, technical, and physical
                safeguards designed to protect the personal information we
                collect. These measures may include:
              </p>
              <ul>
                <li>Using encryption (HTTPS) for data in transit;</li>
                <li>
                  Restricting access to personal information to staff and
                  volunteers who need it;
                </li>
                <li>
                  Using reputable third-party service providers with appropriate
                  security controls; and
                </li>
                <li>Regularly reviewing our practices and systems.</li>
              </ul>
              <p>
                However, no method of transmission over the internet or
                electronic storage is completely secure. We cannot guarantee
                absolute security, and you use the Site at your own risk.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">9. Data Retention</h2>
              <p>
                We retain personal information for as long as reasonably
                necessary to fulfill the purposes described in this Policy,
                including:
              </p>
              <ul>
                <li>To process and acknowledge donations;</li>
                <li>
                  To maintain records for accounting, audit, and legal
                  compliance; and
                </li>
                <li>
                  To manage our relationships with donors and supporters.
                </li>
              </ul>
              <p>
                We may retain certain information for longer periods if required
                by law or if necessary to protect our legal rights. When data is
                no longer needed, we take steps to delete or de-identify it.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                10. Your Choices and Rights
              </h2>
              <p>
                You have choices about how we use your information, and in some
                jurisdictions you may have specific legal rights. Subject to
                applicable law, you may:
              </p>
              <ul>
                <li>
                  Request to review or update the personal information we hold
                  about you;
                </li>
                <li>Request that we correct inaccurate information;</li>
                <li>
                  Request that we delete certain information, subject to legal
                  or contractual obligations;
                </li>
                <li>
                  Opt out of receiving non-essential fundraising or marketing
                  emails; and
                </li>
                <li>
                  Object to or request restriction of certain processing where
                  permitted by law.
                </li>
              </ul>
              <p>
                To exercise your choices, please use the unsubscribe link in our
                emails or contact us using the information in the{" "}
                <a href="#contact-us">How to Contact Us</a> section below. We
                may need to verify your identity before responding to some
                requests.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                11. Children&apos;s Privacy
              </h2>
              <p>
                The Site is not directed to children under 13, and we do not
                knowingly collect personal information from children under 13.
                If you are under 13, please do not use the Site or provide any
                information about yourself.
              </p>
              <p>
                If we learn that we have collected personal information from a
                child under 13, we will take steps to delete that information as
                soon as reasonably possible. If you believe a child under 13 has
                provided personal information to us, please contact us.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                12. International Visitors
              </h2>
              <p>
                Gail Thompson Foundation is based in the United States, and the
                Site is operated from the United States. If you access the Site
                from outside the United States, your information may be
                transferred to, stored in, and processed in the United States or
                other countries that may not provide the same level of data
                protection as the laws in your home country.
              </p>
              <p>
                By using the Site, you acknowledge that your information may be
                transferred to the United States and that we will handle your
                information in accordance with this Policy and applicable law.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                13. Changes to This Policy
              </h2>
              <p>
                We may update this Policy from time to time to reflect changes
                in our practices, the law, or our services. When we make
                changes, we will update the &quot;Last updated&quot; date at the
                top of this page.
              </p>
              <p>
                Material changes will be highlighted in a reasonable manner (for
                example, by posting a notice on the Site). Your continued use of
                the Site after changes are posted means that you accept the
                updated Policy.
              </p>
            </section>

            <section id="contact-us" className="legal-section">
              <h2 className="legal-section__heading">14. How to Contact Us</h2>
              <p>
                If you have questions about this Policy, our privacy practices,
                or your information, please contact us using the information on
                our{" "}
                <a href="/contact" aria-label="Go to our contact page">
                  Contact
                </a>{" "}
                page or at:
              </p>
              <p>
                <strong>Email:</strong>{" "}
                <span>
                  support@gailthompsonfoundation.com
                </span>
                <br />
              </p>
            </section>

            <div className="legal-footer-note">
              This Security &amp; Privacy Policy is provided for general
              informational purposes only and does not constitute legal advice.
              Please consult with a qualified attorney to ensure that this
              Policy accurately reflects your data practices and complies with
              applicable law.
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
